<template>
  <div class="daily-report-form">
    <!-- 기존 일보 목록 -->
    <div v-if="dailyReports.length > 0" class="report-list">
      <el-table :data="dailyReports" size="small" max-height="200" @row-click="editReport">
        <el-table-column prop="report_date" label="날짜" width="110" />
        <el-table-column prop="daily_output" label="금일생산" width="90" align="right">
          <template #default="{ row }">{{ row.daily_output?.toLocaleString() }}</template>
        </el-table-column>
        <el-table-column prop="cumulative_output" label="누적" width="90" align="right">
          <template #default="{ row }">{{ row.cumulative_output?.toLocaleString() }}</template>
        </el-table-column>
        <el-table-column prop="achievement_rate" label="달성률" width="80" align="right">
          <template #default="{ row }">{{ row.achievement_rate }}%</template>
        </el-table-column>
        <el-table-column prop="defect_count" label="불량" width="70" align="right" />
        <el-table-column prop="created_by_name" label="작성자" width="80" />
      </el-table>
    </div>

    <el-divider v-if="dailyReports.length > 0" />

    <!-- 입력 폼 -->
    <el-form :model="form" label-width="90px" size="default">
      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="보고일" required>
            <el-date-picker v-model="form.report_date" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="금일생산">
            <el-input-number v-model="form.daily_output" :min="0" :step="100" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="누적생산">
            <el-input-number v-model="form.cumulative_output" :min="0" :step="100" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="목표수량">
            <el-input-number v-model="form.target_output" :min="0" :step="100" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 달성률 프로그레스 -->
      <div class="achievement-bar">
        <span>달성률: {{ achievementRate }}%</span>
        <el-progress
          :percentage="Math.min(achievementRate, 100)"
          :stroke-width="10"
          :color="achievementRate >= 100 ? '#67C23A' : achievementRate >= 70 ? '#409EFF' : '#E6A23C'"
        />
      </div>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="불량수">
            <el-input-number v-model="form.defect_count" :min="0" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="가동라인">
            <el-input-number v-model="form.working_lines" :min="0" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="투입인원">
            <el-input-number v-model="form.worker_count" :min="0" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 컬러별 생산량 -->
      <el-form-item label="컬러별">
        <div class="color-outputs">
          <div v-for="(co, idx) in form.color_outputs" :key="idx" class="color-row">
            <el-select v-model="co.color_name" filterable allow-create default-first-option
                       placeholder="컬러명" style="width: 140px">
              <el-option v-for="c in mrColorOptions" :key="c" :label="c" :value="c" />
            </el-select>
            <el-input-number v-model="co.output_quantity" :min="0" placeholder="생산" style="width: 120px" />
            <el-input-number v-model="co.defect_quantity" :min="0" placeholder="불량" style="width: 120px" />
            <el-button type="danger" link @click="form.color_outputs.splice(idx, 1)">삭제</el-button>
          </div>
          <el-button type="primary" link @click="addColorOutput">+ 컬러 추가</el-button>
        </div>
      </el-form-item>

      <!-- 라인별 생산실적 -->
      <el-form-item label="라인별">
        <div class="line-outputs">
          <el-table v-if="form.line_outputs.length > 0" :data="form.line_outputs" border size="small" :summary-method="getLineSummary" show-summary>
            <el-table-column label="라인" width="60" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.line_number" :min="1" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="라인명" width="100">
              <template #default="{ row }">
                <el-input v-model="row.line_name" placeholder="Line 1" size="small" />
              </template>
            </el-table-column>
            <el-table-column label="인원" width="70" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.person_count" :min="0" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="시간" width="70" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.working_hours" :min="0" :precision="1" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="총시간" width="70" align="right">
              <template #default="{ row }">
                {{ (row.person_count * row.working_hours).toFixed(0) }}
              </template>
            </el-table-column>
            <el-table-column label="목표" width="80" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.target_output" :min="0" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="실적" width="80" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.actual_output" :min="0" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="달성%" width="60" align="right">
              <template #default="{ row }">
                {{ row.target_output > 0 ? Math.round(row.actual_output / row.target_output * 100) : 0 }}%
              </template>
            </el-table-column>
            <el-table-column label="단가U$" width="80" align="center">
              <template #default="{ row }">
                <el-input-number v-model="row.unit_price_usd" :min="0" :precision="2" :controls="false" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="U$/hr" width="70" align="right">
              <template #default="{ row }">
                {{ calcUsdPerHour(row) }}
              </template>
            </el-table-column>
            <el-table-column label="" width="50" align="center">
              <template #default="{ $index }">
                <el-button type="danger" link size="small" @click="form.line_outputs.splice($index, 1)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="primary" link @click="addLineOutput" style="margin-top: 4px">+ 라인 추가</el-button>
        </div>
      </el-form-item>

      <el-form-item label="요약">
        <el-input v-model="form.summary" type="textarea" :rows="2" placeholder="금일 생산 요약" />
      </el-form-item>

      <el-form-item label="내일계획">
        <el-input v-model="form.tomorrow_plan" type="textarea" :rows="2" placeholder="내일 생산 계획" />
      </el-form-item>
    </el-form>

    <div class="form-footer">
      <el-button v-if="editingId" type="danger" @click="deleteReport" :loading="submitting">삭제</el-button>
      <div style="flex: 1" />
      <el-button @click="resetForm">초기화</el-button>
      <el-button type="primary" @click="saveReport" :loading="submitting">
        {{ editingId ? '수정' : '등록' }}
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

// 컬러명 표기 통일 (2026-08-03): 수량배분 색상을 셀렉트로 — 자유 입력은 유지(allow-create)
const mrColorOptions = ref([])
const loadMrColors = async () => {
  try {
    const { data } = await axiosInstance.get(`/production/main-requests/${props.mainRequestId}/size-quantities/`)
    const rows = Array.isArray(data) ? data : (data?.results || [])
    mrColorOptions.value = [...new Set(rows.map(r => r.color_name).filter(Boolean))].sort()
  } catch { /* 옵션 실패해도 직접 입력 가능 */ }
}

const props = defineProps({
  mainRequestId: { type: [String, Number], required: true },
  dailyReports: { type: Array, default: () => [] },
})

const emit = defineEmits(['saved'])

onMounted(loadMrColors)  // 컬러 셀렉트 옵션 (2026-08-03)

const submitting = ref(false)
const editingId = ref(null)

const today = new Date().toISOString().substring(0, 10)

const form = ref({
  report_date: today,
  daily_output: 0,
  cumulative_output: 0,
  target_output: 0,
  defect_count: 0,
  working_lines: 0,
  worker_count: 0,
  summary: '',
  tomorrow_plan: '',
  color_outputs: [],
  line_outputs: [],
})

const achievementRate = computed(() => {
  if (form.value.target_output > 0) {
    return Math.round(form.value.cumulative_output / form.value.target_output * 100)
  }
  return 0
})

const addColorOutput = () => {
  form.value.color_outputs.push({ color_name: '', output_quantity: 0, defect_quantity: 0 })
}

const addLineOutput = () => {
  const nextNum = form.value.line_outputs.length + 1
  form.value.line_outputs.push({
    line_number: nextNum,
    line_name: '',
    person_count: 0,
    working_hours: 8,
    target_output: 0,
    actual_output: 0,
    defect_count: 0,
    unit_price_usd: 0,
  })
}

const calcUsdPerHour = (row) => {
  const th = row.person_count * row.working_hours
  if (th > 0 && row.unit_price_usd > 0) {
    return (row.actual_output * row.unit_price_usd / th).toFixed(2)
  }
  return '-'
}

const getLineSummary = ({ columns, data }) => {
  const sums = []
  columns.forEach((col, idx) => {
    if (idx === 0) { sums[idx] = '합계'; return }
    if (idx === 1) { sums[idx] = ''; return }
    if (idx === 2) { sums[idx] = data.reduce((s, r) => s + (r.person_count || 0), 0); return }
    if (idx === 4) { sums[idx] = data.reduce((s, r) => s + r.person_count * r.working_hours, 0).toFixed(0); return }
    if (idx === 5) { sums[idx] = data.reduce((s, r) => s + (r.target_output || 0), 0); return }
    if (idx === 6) { sums[idx] = data.reduce((s, r) => s + (r.actual_output || 0), 0); return }
    if (idx === 7) {
      const totalTarget = data.reduce((s, r) => s + (r.target_output || 0), 0)
      const totalActual = data.reduce((s, r) => s + (r.actual_output || 0), 0)
      sums[idx] = totalTarget > 0 ? Math.round(totalActual / totalTarget * 100) + '%' : '-'
      return
    }
    sums[idx] = ''
  })
  return sums
}

const editReport = (row) => {
  editingId.value = row.id
  form.value = {
    report_date: row.report_date,
    daily_output: row.daily_output,
    cumulative_output: row.cumulative_output,
    target_output: row.target_output,
    defect_count: row.defect_count,
    working_lines: row.working_lines,
    worker_count: row.worker_count,
    summary: row.summary || '',
    tomorrow_plan: row.tomorrow_plan || '',
    color_outputs: (row.color_outputs || []).map(co => ({
      color_name: co.color_name,
      output_quantity: co.output_quantity,
      defect_quantity: co.defect_quantity,
    })),
    line_outputs: (row.line_outputs || []).map(lo => ({
      line_number: lo.line_number,
      line_name: lo.line_name || '',
      person_count: lo.person_count || 0,
      working_hours: lo.working_hours || 8,
      target_output: lo.target_output || 0,
      actual_output: lo.actual_output || 0,
      defect_count: lo.defect_count || 0,
      unit_price_usd: lo.unit_price_usd || 0,
    })),
  }
}

const saveReport = async () => {
  if (!form.value.report_date) {
    ElMessage.warning('보고일을 선택하세요.')
    return
  }

  submitting.value = true
  try {
    if (editingId.value) {
      await axiosInstance.put(
        `/production/daily-reports/${editingId.value}/`,
        { ...form.value, main_request: props.mainRequestId }
      )
      ElMessage.success('생산일보가 수정되었습니다.')
    } else {
      await axiosInstance.post(
        `/production/main-requests/${props.mainRequestId}/daily-reports/`,
        form.value
      )
      ElMessage.success('생산일보가 등록되었습니다.')
    }
    emit('saved')
  } catch (error) {
    if (error.response?.status === 400) {
      ElMessage.error('이미 해당 날짜의 일보가 존재합니다.')
    } else {
      ElMessage.error('저장에 실패했습니다.')
    }
  } finally {
    submitting.value = false
  }
}

const deleteReport = async () => {
  try {
    await ElMessageBox.confirm('이 생산일보를 삭제하시겠습니까?', '확인')
    submitting.value = true
    await axiosInstance.delete(`/production/daily-reports/${editingId.value}/`)
    ElMessage.success('삭제되었습니다.')
    resetForm()
    emit('saved')
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('삭제에 실패했습니다.')
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    report_date: today,
    daily_output: 0,
    cumulative_output: 0,
    target_output: 0,
    defect_count: 0,
    working_lines: 0,
    worker_count: 0,
    summary: '',
    tomorrow_plan: '',
    color_outputs: [],
    line_outputs: [],
  }
}
</script>

<style scoped>
.report-list {
  margin-bottom: 10px;
}

.report-list :deep(.el-table__row) {
  cursor: pointer;
}

.achievement-bar {
  padding: 0 0 16px 90px;
  font-size: 13px;
  font-weight: bold;
}

.color-outputs {
  width: 100%;
}

.color-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}
</style>
